import { useMutation, useQueryClient } from '@tanstack/react-query';

import { warehouseApi } from 'api/warehouse';
import { ApiError } from 'shared/errors/ApiError';

import { facilityFeatureFlagsQueryKeys } from './useFacilityFeatureFlagsQuery';

/**
 * Enables or disables a whitelisted facility feature flag.
 * On success the PUT responds with a success envelope only (no flag payload);
 * this mutation invalidates the facility flags query so the list refetches.
 *
 * @param {Omit<
 *   import('@tanstack/react-query').UseMutationOptions<
 *     void,
 *     ApiError,
 *     { facilityId: number|string, feature: string, enabled: boolean }
 *   >,
 *   'mutationFn'
 * >} [mutationOptions={}]
 */
export function useSetFacilityFeatureFlagMutation(mutationOptions = {}) {
    const queryClient = useQueryClient();
    const { onSuccess, ...rest } = mutationOptions;

    return useMutation({
        mutationFn: async ({ enabled, facilityId, feature }) => {
            const response = await warehouseApi.setFacilityFeatureFlag(facilityId, { enabled, feature });
            if (!response?.status) {
                throw new ApiError(
                    response?.msg ?? 'Failed to update facility feature flag',
                    response?.statusCode,
                    response?.data,
                );
            }
        },
        onSuccess: (data, variables, ...args) => {
            queryClient.invalidateQueries({
                queryKey: facilityFeatureFlagsQueryKeys.byFacility(variables.facilityId),
            });
            return onSuccess?.(data, variables, ...args);
        },
        ...rest,
    });
}
